import React, { useRef, useState } from 'react';
import { Icon } from '../../icons';
import { Button } from '../Button/Button';
import './FileUpload.css';

export interface FileUploadProps {
  required?: boolean;
  accept?: string;
  multiple?: boolean;
  dragDropText?: string;
  browseText?: string;
  message?: string;
  messageType?: '' | 'error' | 'success';
  onFilesSelected: (files: File[]) => void;
  onFilesUploaded?: (fileIds: string[]) => void;
  ariaLabel?: string;
}

export const FileUpload = React.memo(function FileUpload({
  required = false,
  accept = '*/*',
  multiple = false,
  dragDropText = 'Drag and drop files here or',
  browseText = 'Browse files',
  message,
  messageType = '',
  onFilesSelected,
  onFilesUploaded,
  ariaLabel,
}: FileUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClick = () => fileInputRef.current?.click();

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) {
      const files = Array.from(e.dataTransfer.files);
      onFilesSelected(files);
      uploadFiles(files);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      onFilesSelected(files);
      uploadFiles(files);
      e.target.value = '';
    }
  };

  const uploadFiles = async (files: File[]) => {
    if (files.length === 0 || isSubmitting) return;
    setIsSubmitting(true);
    const uploadedIds: string[] = [];
    try {
      for (let i = 0; i < files.length; i++) {
        // Mock upload
        await new Promise((resolve) => setTimeout(resolve, 1000));
        uploadedIds.push(`file_${Date.now()}`);
      }
      onFilesUploaded?.(uploadedIds);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className={`file-upload ${isDragging ? 'file-upload--dragging' : ''} ${message && messageType === 'error' ? 'file-upload--error' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
      onKeyDown={(e) => e.key === 'Enter' && handleClick()}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="file-upload__input"
        onChange={handleFileChange}
      />
      <div className="file-upload__content">
        <div className="file-upload__icon">
          <Icon name="CloudUpload" size={36} />
        </div>
        <span className="file-upload__text">
          {dragDropText}
          <Button variant="secondary">{browseText}</Button>
          {required && <span className="file-upload__required">*</span>}
          {message && (
            <span className={`file-upload__message`} data-type={messageType}>
              {message}
            </span>
          )}
        </span>
      </div>
    </div>
  );
});
